/**
 * useMenuSearch - Restaurant menu search state & API calls
 * Extracted from App.js Phase 5 refactoring
 */
import { useState, useCallback } from 'react';
import { isKnownChainRestaurant } from '../config/appConfig';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const useMenuSearch = () => {
  const [showMenuSearch, setShowMenuSearch] = useState(false);
  const [menuSearchQuery, setMenuSearchQuery] = useState('');
  const [menuSearchCity, setMenuSearchCity] = useState('');
  const [menuSearchResults, setMenuSearchResults] = useState(null);
  const [selectedMenuItem, setSelectedMenuItem] = useState(null);
  const [isKnownChain, setIsKnownChain] = useState(false);
  const [showSmartSuggestions, setShowSmartSuggestions] = useState(false);
  const [menuSearchLoading, setMenuSearchLoading] = useState(false);

  const searchRestaurantMenu = useCallback(async (userId) => {
    if (!menuSearchQuery.trim()) {
      alert('Please enter a restaurant name and meal (e.g., "McDonald\'s Big Mac")');
      return;
    }

    const chainDetected = isKnownChainRestaurant(menuSearchQuery);
    if (!chainDetected && !menuSearchCity.trim()) {
      console.log('[Menu Search] Local restaurant detected, city recommended');
    }

    setMenuSearchLoading(true);
    setShowSmartSuggestions(false);
    setSelectedMenuItem(null);
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/smart-picks/search`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          query: menuSearchQuery.trim(),
          city: menuSearchCity.trim() || null,
          user_id: userId
        })
      });

      if (response.ok) {
        const data = await response.json();
        
        if (data.menu_items && data.menu_items.length > 0) {
          setMenuSearchResults({
            ...data,
            is_chain: data.match_type === 'CHAIN',
            data_source: data.match_type === 'CHAIN' ? 'brand_nutrition' : 'estimated'
          });
          setShowSmartSuggestions(false);
        } else {
          setMenuSearchResults(data);
          setShowSmartSuggestions(true);
        }
      } else {
        setMenuSearchResults(null);
        setShowSmartSuggestions(true);
      }
    } catch (error) {
      console.error('Error searching menu:', error);
      setMenuSearchResults(null);
      setShowSmartSuggestions(true);
    } finally {
      setMenuSearchLoading(false);
    }
  }, [menuSearchQuery, menuSearchCity]);

  const handleRestaurantQueryChange = useCallback((value) => {
    setMenuSearchQuery(value);
    setIsKnownChain(isKnownChainRestaurant(value));
  }, []);

  const closeMenuSearch = useCallback(() => {
    setShowMenuSearch(false);
    setMenuSearchQuery('');
    setMenuSearchCity('');
    setMenuSearchResults(null);
    setSelectedMenuItem(null);
    setShowSmartSuggestions(false);
    setIsKnownChain(false);
  }, []);

  return {
    showMenuSearch,
    setShowMenuSearch,
    menuSearchQuery,
    menuSearchCity,
    setMenuSearchCity,
    menuSearchResults,
    setMenuSearchResults,
    selectedMenuItem,
    setSelectedMenuItem,
    isKnownChain,
    showSmartSuggestions,
    menuSearchLoading,
    searchRestaurantMenu,
    handleRestaurantQueryChange,
    closeMenuSearch
  };
};

export default useMenuSearch;
